import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AddBusinessOutlined, BlockOutlined, CheckCircleOutline, SearchOutlined, VisibilityOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useActivateTenant, usePlatformTenants, useSuspendTenant } from '@/hooks/usePlatformTenants';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import DataState from '@/components/common/DataState';
import PageHeader from '@/components/common/PageHeader';
import { useNotify } from '@/components/common/useNotify';
import { TENANT_STATUSES, type GetTenantsParams, type PlatformTenant, type TenantStatus } from '@/types/platform';
import { formatDate } from '@/utils/format';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { platformPath } from '@/config/appTarget';
import ProvisionTenantDialog from '@/pages/platform/ProvisionTenantDialog';

const STATUS_COLOR: Record<TenantStatus, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  Active: 'success',
  Suspended: 'error',
  Pending: 'warning',
  Provisioning: 'info',
  Cancelled: 'default',
};

/**
 * Company (Tenant) List — control-plane registry from GET /api/platform/tenants.
 * Search + status filter + pagination + suspend/activate. Requires a platform session token;
 * if signed in as a tenant user the API returns 401/403 and DataState surfaces it.
 */
export default function CompaniesListPage() {
  const notify = useNotify();
  const [filters, setFilters] = useState<GetTenantsParams>({ page: 1, pageSize: 10 });
  const [searchInput, setSearchInput] = useState('');
  const [confirm, setConfirm] = useState<{ tenant: PlatformTenant; action: 'suspend' | 'activate' } | null>(null);
  const navigate = useNavigate();
  const [provisionOpen, setProvisionOpen] = useState(false);

  const params = useMemo(() => ({ ...filters }), [filters]);
  const query = usePlatformTenants(params);
  const suspend = useSuspendTenant();
  const activate = useActivateTenant();

  const page = query.data;
  const rows = page?.items ?? [];

  const applySearch = () => setFilters((f) => ({ ...f, search: searchInput.trim() || undefined, page: 1 }));

  const runAction = async () => {
    if (!confirm) return;
    try {
      if (confirm.action === 'suspend') await suspend.mutateAsync(confirm.tenant.id);
      else await activate.mutateAsync(confirm.tenant.id);
      notify.success(`Company ${confirm.action === 'suspend' ? 'suspended' : 'activated'}.`);
      setConfirm(null);
    } catch (e) {
      notify.error(e);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Companies"
        subtitle="All companies on the platform."
        breadcrumbs={[{ label: 'Platform' }, { label: 'Companies' }]}
        titleAdornment={
          page ? <Chip size="small" label={page.totalCount} sx={{ fontWeight: 600 }} /> : undefined
        }
        action={
          <Button
            variant="contained"
            startIcon={<AddBusinessOutlined />}
            onClick={() => setProvisionOpen(true)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Add company
          </Button>
        }
      />

      <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0.25, alignItems: 'center' }}>
        Platform-admin view (control plane). Requires a platform owner session.
      </Alert>

      <Card>
        {/* Filters sit in the card's own toolbar, directly above the rows they narrow. */}
        <Grid
          container
          spacing={1.5}
          alignItems="center"
          sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <Grid size={{ xs: 12, sm: 7, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search code, company or email…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              helperText={searchInput.trim() !== (filters.search ?? '') ? 'Press Enter to search' : undefined}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 5, md: 3 }}>
            <TextField
              select fullWidth size="small" label="Status"
              value={filters.status ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, status: (e.target.value || undefined) as TenantStatus | undefined, page: 1 }))}
            >
              <MenuItem value="">All statuses</MenuItem>
              {TENANT_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>

        <DataState
          notFoundAsEmpty
          loading={query.isLoading}
          loadingText="Loading companies…"
          error={query.error}
          isEmpty={!query.isLoading && rows.length === 0}
          emptyTitle="No companies found"
          emptyText="No companies match your filters."
          onRetry={query.refetch}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 860, '& td, & th': { py: 1.25 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Subdomain</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography
                        component={RouterLink}
                        to={platformPath(`/tenants/${t.id}`)}
                        variant="body2"
                        fontWeight={600}
                        noWrap
                        title={t.companyName}
                        sx={{ display: 'block', color: 'text.primary', textDecoration: 'none', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                      >
                        {t.companyName}
                      </Typography>
                      {t.legalName && (
                        <Typography variant="caption" color="text.secondary" noWrap component="div" title={t.legalName}>
                          {t.legalName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{t.code}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography variant="body2" noWrap title={t.email}>{t.email}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.subdomain}</TableCell>
                    <TableCell><Chip size="small" label={t.status} color={STATUS_COLOR[t.status]} variant="outlined" /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(t.createdAt)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => navigate(platformPath(`/tenants/${t.id}`))}>
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {t.status === 'Active' ? (
                        <Tooltip title="Suspend">
                          <IconButton size="small" color="error" onClick={() => setConfirm({ tenant: t, action: 'suspend' })}>
                            <BlockOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <IconButton size="small" color="success" onClick={() => setConfirm({ tenant: t, action: 'activate' })}>
                            <CheckCircleOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {page && (
            <TablePagination
              component="div"
              count={page.totalCount}
              page={(page.page ?? 1) - 1}
              onPageChange={(_, p) => setFilters((f) => ({ ...f, page: p + 1 }))}
              rowsPerPage={page.pageSize}
              onRowsPerPageChange={(e) => setFilters((f) => ({ ...f, pageSize: parseInt(e.target.value, 10), page: 1 }))}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', px: { xs: 1, sm: 2 } },
                '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
              }}
            />
          )}
        </DataState>
      </Card>

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.action === 'suspend' ? 'Suspend company?' : 'Activate company?'}
        message={
          confirm?.action === 'suspend'
            ? `Suspending "${confirm?.tenant.companyName}" blocks its users from signing in.`
            : `Activating "${confirm?.tenant.companyName}" restores access for its users.`
        }
        confirmLabel={confirm?.action === 'suspend' ? 'Suspend' : 'Activate'}
        destructive={confirm?.action === 'suspend'}
        loading={suspend.isPending || activate.isPending}
        onConfirm={runAction}
        onClose={() => setConfirm(null)}
      />

      <ProvisionTenantDialog open={provisionOpen} onClose={() => setProvisionOpen(false)} onDone={() => query.refetch()} />
    </Box>
  );
}
